/**
 * Custom Measurements Handler - Professional Version
 * With integrated lining handler (NO DUPLICATES)
 */

// Filter out unnecessary warnings from Shopify theme
(function() {
  if (!window._warningFilterInstalled) {
    const originalWarn = console.warn;
    console.warn = function(...args) {
      const message = args[0] ? String(args[0]) : '';
      // Hide "No Type selected" warnings from Shopify theme
      if (message.includes('Type selected') || message.includes('type selected')) {
        return;
      }
      originalWarn.apply(console, args);
    };
    window._warningFilterInstalled = true;
  }
})();

class CustomMeasurementsHandler {
  constructor() {
    this.initialized = false;
    this.formSubmitEvent = null;
    this.boundValidateMeasurements = (e) => this.validateMeasurements(e);
  }

  init() {
    if (this.initialized) return;
    
    this.waitForProductInfo(() => {
      this.setupElements();
      this.setupEventListeners();
      this.setupGuideModals();
      this.setupInputValidation();
      this.setupLiningCheckboxHandler(); // NEW: Handle checkbox changes
      this.initialized = true;
    });
  }

  waitForProductInfo(callback) {
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.productForm) {
      requestAnimationFrame(callback);
    } else {
      document.addEventListener('product-info:loaded', () => {
        requestAnimationFrame(callback);
      }, { once: true });
    }
  }

  setupElements() {
    this.measurementsSection = document.querySelector('[data-measurements-section]');
    if (!this.measurementsSection) return;

    this.requiredFields = this.measurementsSection.querySelectorAll('.measurement-required');
    this.errorMessage = this.measurementsSection.querySelector('[data-error-message]');
    this.productForm = this.measurementsSection.closest('form');
    this.confirmationModal = document.querySelector('[data-confirmation-modal]');
    this.confirmButton = document.querySelector('[data-confirm-add]');
    this.editButton = document.querySelector('[data-edit-measurements]');
    this.summaryContainer = document.querySelector('[data-measurement-summary]');
    this.liningCheckbox = document.querySelector('[data-lining-addon]');
    
    // NEW: Get product data for variant switching
    this.getProductData();
  }

  setupEventListeners() {
    if (!this.productForm || !this.confirmationModal) return;

    this.productForm.addEventListener('submit', this.boundValidateMeasurements, true);

    if (this.confirmButton) {
      this.confirmButton.addEventListener('click', () => this.confirmAndSubmit());
    }

    if (this.editButton) {
      this.editButton.addEventListener('click', () => this.editMeasurements());
    }

    this.confirmationModal.addEventListener('click', (e) => {
      if (e.target === this.confirmationModal) {
        this.hideModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.confirmationModal.classList.contains('show')) {
        this.hideModal();
      }
    });

    this.requiredFields.forEach(field => {
      field.addEventListener('input', () => this.removeFieldError(field));
      field.addEventListener('change', () => this.removeFieldError(field));
    });
  }

  validateMeasurements(event) {
    let isValid = true;
    let emptyFields = [];

    this.requiredFields.forEach(field => {
      const value = field.value.trim();
      if (!value || value === '' || value === 'Please select') {
        field.classList.add('error');
        isValid = false;
        emptyFields.push(field);
      } else {
        field.classList.remove('error');
      }
    });

    if (!isValid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.errorMessage.classList.add('show');

      if (emptyFields.length > 0) {
        emptyFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => emptyFields[0].focus(), 300);
      }

      return false;
    } else {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.formSubmitEvent = event;
      this.showConfirmationModal();
      return false;
    }
  }

  showConfirmationModal() {
    this.errorMessage.classList.remove('show');

    let summaryHTML = '';

    this.requiredFields.forEach(field => {
      const label = field.closest('.measurement-group').querySelector('.measurement-label').childNodes[0].textContent.trim();
      let value = field.value;

      if (field.tagName === 'INPUT') {
        const unit = field.closest('.input-wrapper')?.querySelector('.measurement-unit');
        if (unit) {
          value += ' ' + unit.textContent.trim();
        }
      }

      summaryHTML += `
        <div class="summary-item">
          <span class="summary-label">${label}</span>
          <span class="summary-value">${value}</span>
        </div>
      `;
    });

    const notesField = document.querySelector('[name="properties[Additional Notes]"]');
    if (notesField && notesField.value.trim()) {
      summaryHTML += `
        <div class="summary-item">
          <span class="summary-label">Additional Notes</span>
          <span class="summary-value">${notesField.value.trim()}</span>
        </div>
      `;
    }

    // Show lining status
    if (this.liningCheckbox && this.liningCheckbox.checked) {
      summaryHTML += `
        <div class="summary-item">
          <span class="summary-label">Lining</span>
          <span class="summary-value">✓ With Lining (+200 AED)</span>
        </div>
      `;
    }

    this.summaryContainer.innerHTML = summaryHTML;
    this.confirmationModal.classList.add('show');
    document.body.style.overflow = 'hidden';
  }

  hideModal() {
    this.confirmationModal.classList.remove('show');
    document.body.style.overflow = '';
  }

  confirmAndSubmit() {
    this.hideModal();

    // Check if lining is selected
    const hasLining = this.liningCheckbox && this.liningCheckbox.checked;
    
    if (hasLining) {
      console.log('✅ Lining selected, switching variant');
      this.switchToLiningVariant();
    } else {
      console.log('✅ No lining, using current variant');
    }
    
    // Submit with the correct variant
    this.normalSubmit();
  }

  normalSubmit() {
    // Shorten labels and add units
    this.requiredFields.forEach(field => {
      let originalName = field.name;
      let labelText = field.closest('.measurement-group').querySelector('.measurement-label').childNodes[0].textContent.trim();
      
      if (labelText === 'Sleeve Length from Neck') {
        labelText = 'Sleeve';
      }
      labelText = labelText.replace(' Size', '');
      
      field.dataset.originalName = originalName;
      field.name = `properties[${labelText}]`;
      
      if (field.tagName === 'INPUT' && field.type === 'text') {
        const unit = field.closest('.input-wrapper')?.querySelector('.measurement-unit');
        if (unit && field.value && !field.value.includes(unit.textContent.trim())) {
          field.dataset.originalValue = field.value;
          field.value = field.value + ' ' + unit.textContent.trim();
        }
      }
    });
    
    const notesField = document.querySelector('[name="properties[Additional Notes]"]');
    if (notesField && notesField.value.trim()) {
      notesField.dataset.originalName = notesField.name;
      notesField.name = 'properties[Notes]';
    }

    this.productForm.removeEventListener('submit', this.boundValidateMeasurements, true);
    
    const newSubmitEvent = new Event('submit', { 
      bubbles: true, 
      cancelable: true 
    });
    this.productForm.dispatchEvent(newSubmitEvent);
    
    setTimeout(() => {
      this.requiredFields.forEach(field => {
        if (field.dataset.originalName) {
          field.name = field.dataset.originalName;
          delete field.dataset.originalName;
        }
        if (field.dataset.originalValue) {
          field.value = field.dataset.originalValue;
          delete field.dataset.originalValue;
        }
      });
      
      if (notesField && notesField.dataset.originalName) {
        notesField.name = notesField.dataset.originalName;
        delete notesField.dataset.originalName;
      }
      
      this.productForm.addEventListener('submit', this.boundValidateMeasurements, true);
    }, 500);
  }

  // NEW: Get product data from the page
  getProductData() {
    // Try to get from product-info element
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      // Check for variant radios/selects
      const variantPicker = document.querySelector('variant-radios, variant-selects');
      if (variantPicker && variantPicker.getVariantData) {
        this.productData = variantPicker.getVariantData();
        return;
      }
    }

    // Try to get from window object (some themes expose it)
    if (window.product && window.product.variants) {
      this.productData = window.product;
      return;
    }

    // Product data not found - using direct radio button method instead
    // console.warn('⚠️ Product data not found - variant switching may not work');
  }

  // NEW: Setup lining checkbox to switch variants automatically
  setupLiningCheckboxHandler() {
    if (!this.liningCheckbox) return;

    // When checkbox changes, update radio
    this.liningCheckbox.addEventListener('change', (e) => {
      this.handleLiningChange(e.target.checked);
    });

    // FORCE Without Lining on every page load
    const forceWithoutLining = () => {
      // Find all Lining radios
      const allLiningRadios = document.querySelectorAll('input[name^="Lining"]');
      
      // Find "Without Lining" (case insensitive)
      let withoutLiningRadio = null;
      allLiningRadios.forEach(radio => {
        if (radio.value.toLowerCase().includes('without')) {
          withoutLiningRadio = radio;
        }
      });
      
      if (withoutLiningRadio && !withoutLiningRadio.checked) {
        console.log('🔄 Forcing Without Lining variant');
        withoutLiningRadio.checked = true;
        withoutLiningRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Always uncheck the checkbox
      if (this.liningCheckbox.checked) {
        this.liningCheckbox.checked = false;
      }
    };

    // Force multiple times
    forceWithoutLining();
    setTimeout(forceWithoutLining, 50);
    setTimeout(forceWithoutLining, 100);
    setTimeout(forceWithoutLining, 200);
    setTimeout(forceWithoutLining, 500);

    console.log('✅ Lining checkbox handler setup - force Without Lining enabled');
  }

  // NEW: Handle lining checkbox change
  handleLiningChange(addLining) {
    console.log('🔄 Lining checkbox changed:', addLining ? 'WITH Lining' : 'WITHOUT Lining');
    
    // Find the Lining radio button (case insensitive search)
    const allLiningRadios = document.querySelectorAll('input[name^="Lining"]');
    console.log('📻 Found', allLiningRadios.length, 'Lining radios');
    
    const targetValue = addLining ? 'with lining' : 'without lining';
    
    let liningRadio = null;
    allLiningRadios.forEach(radio => {
      console.log('  Checking radio:', radio.value);
      if (radio.value.toLowerCase() === targetValue) {
        liningRadio = radio;
      }
    });
    
    if (!liningRadio) {
      console.error('❌ Lining radio not found for:', targetValue);
      console.error('Available radios:', Array.from(allLiningRadios).map(r => r.value));
      return;
    }
    
    console.log('✅ Found Lining radio button:', liningRadio.value);
    
    // Check it
    liningRadio.checked = true;
    
    // Trigger Shopify's native change event
    liningRadio.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Also trigger on parent elements for Dawn theme
    const variantSelects = liningRadio.closest('variant-selects');
    if (variantSelects) {
      variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    console.log('✅ Radio checked and events dispatched');
    
    // Wait for Shopify to update price
    setTimeout(() => {
      const priceElement = document.querySelector('.price-item--regular, .price__regular .price-item');
      if (priceElement) {
        console.log('💰 Price updated to:', priceElement.textContent);
      }
    }, 200);
  }

  // NEW: Switch to "With Lining" variant (called on submit)
  switchToLiningVariant() {
    this.handleLiningChange(true);
  }

  // NEW: Get current variant
  getCurrentVariant() {
    // Try variant picker first
    const variantPicker = document.querySelector('variant-radios') || 
                         document.querySelector('variant-selects');
    
    if (variantPicker && variantPicker.currentVariant) {
      return variantPicker.currentVariant;
    }

    // Fallback: get from hidden input
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      const variantId = parseInt(variantInput.value);
      
      // Try to find in productData
      if (this.productData && this.productData.variants) {
        return this.productData.variants.find(v => v.id === variantId);
      }

      // Return basic variant info
      return { id: variantId };
    }

    return null;
  }

  // NEW: Check if variant has lining
  variantHasLining(variant) {
    const liningValue = this.getOptionValue(variant, 'Lining');
    return liningValue === 'With Lining';
  }

  // NEW: Get option value from variant
  getOptionValue(variant, optionName) {
    if (!variant) return null;

    // Try variant.options array with product.options
    if (this.productData && this.productData.options && variant.options) {
      const optionIndex = this.productData.options.indexOf(optionName);
      if (optionIndex !== -1) {
        return variant.options[optionIndex];
      }
    }

    // Try variant object properties
    if (variant.option1 && optionName === 'Type') return variant.option1;
    if (variant.option2 && optionName === 'Lining') return variant.option2;
    if (variant.option3) return variant.option3;

    return null;
  }

  // NEW: Find variant by options
  findVariantByOptions(options) {
    if (!this.productData || !this.productData.variants) {
      // Product data not available - using direct radio button method
      // console.warn('⚠️ Product data not available for variant search');
      return null;
    }

    return this.productData.variants.find(variant => {
      return Object.keys(options).every(optionName => {
        const optionValue = this.getOptionValue(variant, optionName);
        return optionValue === options[optionName];
      });
    });
  }

  // NEW: Select variant
  selectVariant(variantId) {
    console.log('🎯 Selecting variant:', variantId);

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);

    // Update hidden input
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update variant picker state
    const variantPicker = document.querySelector('variant-radios') || 
                         document.querySelector('variant-selects');
    if (variantPicker && this.productData) {
      const newVariant = this.productData.variants.find(v => v.id == variantId);
      if (newVariant && variantPicker.currentVariant) {
        variantPicker.currentVariant = newVariant;
      }
    }
  }

  // NEW: Update price display
  updatePriceDisplay(price) {
    const priceElements = document.querySelectorAll('.price-item--regular, .price__regular .price-item');
    const formattedPrice = (price / 100).toFixed(2);
    
    priceElements.forEach(element => {
      if (element.textContent.includes('AED')) {
        element.textContent = formattedPrice + ' AED';
      } else {
        element.textContent = 'AED ' + formattedPrice;
      }
    });

    console.log('💰 Price updated:', formattedPrice, 'AED');
  }

  editMeasurements() {
    this.hideModal();
    const firstField = this.requiredFields[0];
    if (firstField) {
      firstField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => firstField.focus(), 300);
    }
  }

  removeFieldError(field) {
    const value = field.value.trim();
    if (value && value !== 'Please select') {
      field.classList.remove('error');

      const allFilled = Array.from(this.requiredFields).every(f => {
        const val = f.value.trim();
        return val && val !== '' && val !== 'Please select';
      });

      if (allFilled) {
        this.errorMessage.classList.remove('show');
      }
    }
  }

  setupGuideModals() {
    const guideIcons = document.querySelectorAll('[data-guide]');
    const unifiedModal = document.querySelector('[data-unified-guide-modal]');
    const guideCloseButton = document.querySelector('[data-close-guide]');
    const guideTitle = document.querySelector('[data-guide-title]');
    const guideDescription = document.querySelector('[data-guide-description]');
    const guideTips = document.querySelector('[data-guide-tips]');
    const guideImage = document.querySelector('[data-guide-image]');

    if (!guideIcons.length || !unifiedModal) return;

    const FF_GUIDE_AR = {
          "abaya-length": {
                "title": "طريقة قياس طول العباية",
                "description": "قفي مستقيمة بمحاذاة الحائط. قيسي من أعلى الكتف نزولاً بشكل مستقيم حتى المكان الذي تريدين أن تنتهي عنده العباية.",
                "tips": [
                      "ارتدي الحذاء الذي تلبسينه عادةً مع العباية",
                      "قفي بشكل طبيعي دون انحناء",
                      "استعيني بشخص آخر للحصول على قياس أدق",
                      "قيسي من الجهتين واعتمدي المتوسط"
                ]
          },
          "bust": {
                "title": "طريقة قياس محيط الصدر",
                "description": "لفّي شريط القياس حول أعرض جزء من الصدر. اجعلي الشريط موازياً للأرض، ومشدوداً بلطف دون ضغط.",
                "tips": [
                      "ارتدي حمالة صدر مناسبة",
                      "اجعلي الشريط موازياً للأرض",
                      "تنفّسي بشكل طبيعي ولا تحبسي أنفاسك",
                      "يكون الشريط ملاصقاً دون أن يضغط"
                ]
          },
          "weight": {
                "title": "لماذا نحتاج وزنكِ؟",
                "description": "يساعدنا وزنكِ على ترشيح المقاس الأنسب وضمان انسدال القماش بالشكل الصحيح لأقصى راحة وأناقة.",
                "tips": [
                      "يضمن هامش القماش المناسب",
                      "يساعد في تحديد أنسب قَصّة",
                      "يضمن راحة أكبر عند الارتداء",
                      "جميع المعلومات تبقى خاصة وآمنة"
                ]
          },
          "sleeve": {
                "title": "طريقة قياس طول الكم من الرقبة",
                "description": "أبقي ذراعكِ منثنية قليلاً بشكل طبيعي. قيسي من فتحة الرقبة، مروراً بالكتف، نزولاً على امتداد الذراع حتى المعصم.",
                "tips": [
                      "أبقي ذراعكِ منثنية قليلاً بشكل طبيعي",
                      "ابدئي من منطقة الرقبة",
                      "قيسي من الجهة الخارجية للذراع",
                      "انتهي عند عظمة المعصم، لا عند اليد"
                ]
          },
          "scarf-size": {
                "title": "طريقة قياس مقاس الشيلة",
                "description": "قيسي الشيلة من حافة إلى الحافة المقابلة لتحديد المقاس.",
                "tips": [
                      "افرشي الشيلة على سطح مستوٍ",
                      "قيسي قطرياً من زاوية إلى الزاوية المقابلة",
                      "اختاري المقاس الذي يناسب تفضيلكِ",
                      "المقاسات الأكبر توفّر تغطية أوسع"
                ]
          }
    };
    const guideContent = {
      'abaya-length': {
        title: 'How to Measure Abaya Length',
        description: 'Stand straight against a wall. Measure from the top of your shoulder straight down to the floor where you want the abaya to end.',
        tips: [
          'Wear the shoes you typically wear with abayas',
          'Stand naturally without slouching',
          'Ask someone to help you measure for best accuracy',
          'Measure on both sides and use the average'
        ],
        image: 'https://cdn.shopify.com/s/files/1/0801/0796/2586/files/Abaya_Length.png?v=1767721148'
      },
      'bust': {
        title: 'How to Measure Bust',
        description: 'Wrap a measuring tape around the fullest part of your bust. Keep the tape parallel to the ground and ensure it\'s snug but not too tight.',
        tips: [
          'Wear a well-fitted bra',
          'Keep tape parallel to the floor',
          'Breathe normally, don\'t hold your breath',
          'Tape should be snug but not tight'
        ],
        image: 'https://cdn.shopify.com/s/files/1/0801/0796/2586/files/Bust.png?v=1767727138'
      },
      'weight': {
        title: 'Why We Need Your Weight',
        description: 'Your weight helps us recommend the best fit and ensure proper draping of the fabric for maximum comfort and elegance.',
        tips: [
          'Ensures proper fabric allowance',
          'Helps determine best fit style',
          'Guarantees comfortable wearing',
          'All information is kept private and secure'
        ],
        image: 'https://cdn.shopify.com/s/files/1/0801/0796/2586/files/Weight.png?v=1767721148'
      },
      'sleeve': {
        title: 'How to Measure Sleeve Length from Neck',
        description: 'Hold your arm slightly bent naturally. Measure from the neckline, over your shoulder, down the outside of your arm to your wrist bone.',
        tips: [
          'Keep your arm slightly bent naturally',
          'Start from the neckline/collar area',
          'Measure on the outside of the arm',
          'End at the wrist bone, not the hand'
        ],
        image: 'https://cdn.shopify.com/s/files/1/0801/0796/2586/files/Sleeve_Length_from_Neck.png?v=1767721148'
      },
      'scarf-size': {
        title: 'How to Measure Scarf Size',
        description: 'Measure the scarf from one edge to the opposite edge to determine the size. The standard scarf sizes range from 22 to 28 inches.',
        tips: [
          'Lay the scarf flat on a surface',
          'Measure diagonally from corner to corner',
          'Choose a size that matches your preference',
          'Larger sizes provide more coverage'
        ],
        image: 'https://cdn.shopify.com/s/files/1/0801/0796/2586/files/Scarf_Size.png?v=1767822709'
      }
    };

    const showGuideModal = (guideType) => {
      let content = guideContent[guideType];
      if (!content) return;
      // عربي: استبدال نصوص العرض فقط — المفاتيح والصور كما هي
      if ((document.documentElement.lang || '').toLowerCase().startsWith('ar') && FF_GUIDE_AR[guideType]) {
        content = Object.assign({}, content, FF_GUIDE_AR[guideType]);
      }

      const scrollY = window.scrollY;

      if (guideTitle) guideTitle.textContent = content.title;
      if (guideDescription) guideDescription.textContent = content.description;

      if (guideImage) {
        guideImage.src = content.image;
        guideImage.alt = content.title;
      }

      if (guideTips) {
        guideTips.innerHTML = '';
        content.tips.forEach(tip => {
          const li = document.createElement('li');
          li.textContent = tip;
          guideTips.appendChild(li);
        });
      }

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      unifiedModal.classList.add('show');
    };

    const closeGuideModal = () => {
      unifiedModal.classList.add('hiding');
      setTimeout(() => {
        unifiedModal.classList.remove('show', 'hiding');

        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }, 250);
    };

    guideIcons.forEach(icon => {
      icon.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const guideType = this.getAttribute('data-guide');
        showGuideModal(guideType);
      });
    });

    if (guideCloseButton) {
      guideCloseButton.addEventListener('click', closeGuideModal);
    }

    unifiedModal.addEventListener('click', (e) => {
      if (e.target === unifiedModal) {
        closeGuideModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && unifiedModal && unifiedModal.classList.contains('show')) {
        closeGuideModal();
      }
    });
  }

  setupInputValidation() {
    const numberInputs = document.querySelectorAll('.measurement-input[type="text"]');
    
    numberInputs.forEach(input => {
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('lang', 'en');

      input.addEventListener('focus', function() {
        this.setAttribute('lang', 'en');
        this.setAttribute('dir', 'ltr');
      });

      input.addEventListener('input', function() {
        let value = this.value;
        value = value.replace(/[^\d.]/g, '');
        
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }
        
        this.value = value;
      });

      input.addEventListener('paste', function(e) {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const cleanText = pastedText.replace(/[^\d.]/g, '');
        
        const start = this.selectionStart;
        const end = this.selectionEnd;
        const currentValue = this.value;
        
        this.value = currentValue.substring(0, start) + cleanText + currentValue.substring(end);
        this.selectionStart = this.selectionEnd = start + cleanText.length;
        this.dispatchEvent(new Event('input'));
      });

      input.addEventListener('keypress', function(e) {
        const char = String.fromCharCode(e.which);
        if (!/[\d.]/.test(char)) {
          e.preventDefault();
        }
      });
    });

    const textarea = document.querySelector('.measurement-textarea');
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.minHeight = '10rem';
      textarea.style.resize = 'none';
      textarea.style.overflow = 'hidden';

      textarea.addEventListener('input', function() {
        this.style.height = 'auto';
        this.style.height = Math.max(this.scrollHeight, 100) + 'px';
      });

      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, 100) + 'px';
    }
  }
}

// Initialize ONCE
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.measurementsHandlerInitialized) {
      window.measurementsHandlerInitialized = true;
      const handler = new CustomMeasurementsHandler();
      handler.init();
      console.log('✅ Measurements handler initialized (Variant Switching Version)');
    }
  });
} else {
  if (!window.measurementsHandlerInitialized) {
    window.measurementsHandlerInitialized = true;
    const handler = new CustomMeasurementsHandler();
    handler.init();
    console.log('✅ Measurements handler initialized (Variant Switching Version)');
  }
}